import * as React from 'react';

import { Box, Avatar as MuiAvatar, AvatarProps as MuiAvatarProps, Tooltip } from '@mui/material';

import { AvatarSize, SHADCN_COLORS, theme } from '@itp/talent/theme';
import { replaceImageStorage } from '@itp/talent/utils/imageLoader';

import { LoaderOverlay, StyledBadge, StyledProgress } from './avatar.styles';

interface AvatarProps extends MuiAvatarProps {
  children?: React.ReactNode;
  loading?: boolean;
  online?: boolean;
  score?: number;
  size?: AvatarSize;
}

const MAX_RETRIES = 5;

const CIRCULAR_PROGRESS_SIZING = {
  large: 48,
  medium: 24,
  small: 16,
};

const SCORE_RING_GAP = 4;
const SCORE_RING_STROKE = 3;

const AVATAR_PX: Record<AvatarSize, number> = {
  large: 72,
  medium: 40,
  small: 28,
  xlarge: 152,
};

const ScoreRing: React.FC<{ score: number; size: AvatarSize }> = ({ score, size }) => {
  const avatarPx = AVATAR_PX[size];
  const ringRadius = avatarPx / 2 + SCORE_RING_GAP;
  const svgSize = ringRadius * 2 + SCORE_RING_STROKE;
  const center = svgSize / 2;
  const circumference = 2 * Math.PI * ringRadius;
  const clampedScore = Math.max(0, Math.min(100, score));
  const offset = circumference - (clampedScore / 100) * circumference;

  return (
    <svg
      style={{
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%) rotate(-90deg)',
      }}
      height={svgSize}
      width={svgSize}
    >
      <circle
        cx={center}
        cy={center}
        fill="none"
        r={ringRadius}
        stroke={SHADCN_COLORS.border}
        strokeWidth={SCORE_RING_STROKE}
      />
      <circle
        cx={center}
        cy={center}
        fill="none"
        r={ringRadius}
        stroke={SHADCN_COLORS.foreground}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        strokeWidth={SCORE_RING_STROKE}
      />
    </svg>
  );
};

export const Avatar: React.FC<AvatarProps> = ({
  children,
  loading: propsLoading,
  online = undefined,
  score,
  size = 'large',
  src = '',
  ...props
}) => {
  const [url, setUrl] = React.useState('');
  const retryCountRef = React.useRef(0);
  const [loading, setLoading] = React.useState(false);

  const errorHandler = () => {
    setLoading(true);
    if (retryCountRef.current < MAX_RETRIES) {
      const delay = Math.pow(2, retryCountRef.current) * 1000;
      setTimeout(() => {
        setUrl(src + '?retry=' + Date.now());
        retryCountRef.current += 1;
      }, delay);
    } else {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (src) {
      setLoading(true);
      setUrl(src);
    }
    retryCountRef.current = 0;
  }, [src]);

  const avatar = (
    <MuiAvatar
      src={replaceImageStorage(url)}
      variant="circular"
      {...props}
      slotProps={{
        img: {
          crossOrigin: 'anonymous',
          onError: errorHandler,
          onLoad: () => {
            setLoading(false);
          },
          sx: { objectFit: 'contain' },
        },
      }}
      sx={{ ...theme?.avatarSizes?.[size], aspectRatio: '1' }}
    >
      {children}
    </MuiAvatar>
  );

  const ringPadding = typeof score === 'number' ? SCORE_RING_GAP + SCORE_RING_STROKE : 0;

  return (
    <Box
      display="inline-block"
      height="fit-content"
      position="relative"
      sx={{ padding: `${ringPadding}px` }}
    >
      {typeof online !== 'undefined' ? (
        <StyledBadge
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          online={online}
          overlap="circular"
          variant="dot"
        >
          {avatar}
        </StyledBadge>
      ) : (
        avatar
      )}
      {typeof score === 'number' && (
        <Tooltip arrow placement="bottom" title={`Profile completeness: ${score}%`}>
          <Box sx={{ inset: 0, position: 'absolute' }}>
            <ScoreRing score={score} size={size} />
          </Box>
        </Tooltip>
      )}
      {(loading || propsLoading) && (
        <LoaderOverlay
          bgcolor="grey.300"
          sx={{ aspectRatio: '1' }}
          {...theme?.avatarSizes?.[size]}
          variant={props.variant || 'circular'}
        >
          <StyledProgress size={CIRCULAR_PROGRESS_SIZING[size]} />
        </LoaderOverlay>
      )}
    </Box>
  );
};
